import {IncomingHttpHeaders} from 'http';
import * as Sentry from '@sentry/nextjs';
import {isBrowserContext} from 'core/helpers';
import {randomUUID} from 'crypto';

export const SSR_REQUEST_ID_PROP = 'ssrRequestId';

export const REQUEST_ID_HEADER = 'X-Request-ID';

const REQUEST_ID_INCOMING_HEADER = 'x-request-id';

export interface ISSRRequestIdPageProps {
    [SSR_REQUEST_ID_PROP]?: string | null;
}

interface IRequestIdAls {
    getStore: () => string | undefined;
    run: <T>(store: string, callback: () => T) => T;
}

interface IBindableResponse {
    setHeader: (name: string, value: string) => void;
}

function createServerStorage(): IRequestIdAls | null {
    if (isBrowserContext()) {
        return null;
    }

    try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports,no-inline-comments
        const {AsyncLocalStorage} = require(/* webpackIgnore: true */ 'async_hooks') as typeof import('async_hooks');

        return new AsyncLocalStorage<string>();
    } catch {
        return null;
    }
}

const serverStorage = createServerStorage();
let clientRequestId: string | null = null;

function normalizeRequestId(value: string | string[] | undefined): string | null {
    const raw = Array.isArray(value) ? value[0] : value;

    if ('string' !== typeof raw) {
        return null;
    }

    const trimmed = raw.trim();

    return trimmed || null;
}

function tagSentry(requestId: string | null): void {
    const scope = Sentry.getCurrentScope?.();

    if (!scope || !requestId) {
        return;
    }

    scope.setTag('request_id', requestId);
}

export function readRequestIdFromHeaders(headers: IncomingHttpHeaders | undefined | null): string | null {
    if (!headers) {
        return null;
    }

    return normalizeRequestId(headers[REQUEST_ID_INCOMING_HEADER]);
}

export function writeRequestIdHeader(res: IBindableResponse | undefined, requestId: string | null): void {
    if (!res) {
        return;
    }

    res.setHeader(REQUEST_ID_HEADER, requestId ?? '');
}

/**
 * берёт текущий request_id на сервере или клиенте уже
 */
export function getRequestId(): string | null {
    if (isBrowserContext()) {
        return clientRequestId;
    }

    return serverStorage?.getStore() ?? null;
}

/**
 * сохраняет request_id страницы в браузере после SSR или клиентской навигации
 */
export function hydrateRequestId(requestId: string | null | undefined): void {
    if (!isBrowserContext()) {
        return;
    }

    clientRequestId = requestId ?? null;
    tagSentry(clientRequestId);
}

/**
 * выполняет коллбек в контексте request_id (изоляция параллельных SSR-запросов)
 * если nginx не прислал заголовок — коллбек выполняем без контекста
 */
export function runWithRequestId<T>(requestId: string | null | undefined, fn: () => T): T {
    const id = normalizeRequestId(requestId ?? undefined);

    if (isBrowserContext()) {
        if (id) {
            hydrateRequestId(id);
        }

        return fn();
    }

    if (!serverStorage || !id) {
        tagSentry(id);

        return fn();
    }

    return serverStorage.run(id, () => {
        tagSentry(id);

        return fn();
    });
}

/**
 * читает X-Request-ID из хедеров реквеста, проставляет его в ответ и выполняет коллбек
 */
export function withRequestIdContext<T>(
    _req: {headers?: IncomingHttpHeaders} | undefined,
    res: IBindableResponse | undefined,
    fn: () => T,
): T {
    const requestId = randomUUID();
    // const requestId = readRequestIdFromHeaders(req?.headers);

    writeRequestIdHeader(res, requestId);

    return runWithRequestId(requestId, fn);
}

export function attachRequestIdToSentryEvent<T extends {tags?: Record<string, unknown>}>(event: T): T {
    const requestId = getRequestId();

    if (!requestId) {
        return event;
    }

    return {
        ...event,
        tags: {
            ...event.tags,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            request_id: requestId,
        },
    };
}
